import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { brl, STATUS_LABELS, STATUS_TONES, timeBR } from "@/lib/format";
import { toast } from "sonner";
import { MessageCircle, Printer, Pencil } from "lucide-react";
import { printOrder } from "@/lib/print";
import { OrderEditDialog } from "@/components/Tables";
import { useAuth } from "@/context/AuthContext";

const FLOW = ["recebido", "confirmado", "preparando", "pronto", "saiu", "entregue"];

export default function OrdersBoard() {
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [products, setProducts] = useState([]);
  const { restaurant } = useAuth();
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  const load = async () => setOrders((await api.get("/orders")).data);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  const advance = async (o) => {
    const idx = FLOW.indexOf(o.status);
    const next = FLOW[Math.min(idx + 1, FLOW.length - 1)];
    if (o.status === "entregue" || o.status === "cancelado") return;
    await api.patch(`/orders/${o.id}/status`, { status: next });
    toast.success(`Pedido #${o.number} → ${STATUS_LABELS[next]}`); load();
  };
  const cancel = async (o) => { await api.patch(`/orders/${o.id}/status`, { status: "cancelado" }); load(); };
  const whatsapp = async (o) => {
    try {
      const r = await api.get(`/orders/${o.id}/whatsapp`);
      if (!r.data.link) return toast.error("Cliente sem telefone válido");
      window.open(r.data.link, "_blank");
    } catch { toast.error("Erro ao gerar link"); }
  };

  const cols = [
    { key: "recebido", label: "Recebidos" },
    { key: "preparando", label: "Preparando" },
    { key: "pronto", label: "Prontos" },
    { key: "entregue", label: "Entregues" },
  ];

  return (
    <div className="space-y-6" data-testid="orders-page">
      <h1 className="font-display text-3xl font-extrabold text-stone-900">Pedidos em tempo real</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {cols.map((c) => {
          const os = orders.filter((o) => (c.key === "recebido" ? ["recebido", "confirmado"] : c.key === "entregue" ? ["entregue", "saiu"] : [c.key]).includes(o.status));
          return (
            <div key={c.key} className="rounded-2xl bg-white border border-stone-200 p-4 min-h-[400px]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-stone-900">{c.label}</h3>
                <Badge variant="secondary">{os.length}</Badge>
              </div>
              <div className="space-y-2">
                {os.map((o) => (
                  <div key={o.id} className={`rounded-xl border p-3 ${STATUS_TONES[o.status]}`} data-testid={`order-${o.number}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display font-extrabold">#{o.number}</div>
                      <div className="text-xs">{timeBR(o.created_at)}</div>
                    </div>
                    <div className="text-sm mt-1">{o.customer_name || o.channel}</div>
                    <div className="text-xs mt-1">{o.items.length} itens • <span className="font-mono-num font-bold">{brl(o.total)}</span></div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {o.status !== "entregue" && o.status !== "cancelado" && (
                        <Button size="sm" className="h-7 bg-stone-900 hover:bg-stone-800" onClick={() => advance(o)} data-testid={`advance-${o.number}`}>Avançar</Button>
                      )}
                      <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => printOrder(o, restaurant)} data-testid={`print-${o.number}`}><Printer className="h-3 w-3" /> Imprimir</Button>
                      {o.status !== "entregue" && o.status !== "cancelado" && (
                        <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setEditing(o)} data-testid={`edit-${o.number}`}><Pencil className="h-3 w-3" /> Editar</Button>
                      )}
                      {["pronto", "saiu"].includes(o.status) && o.customer_phone && (
                        <Button size="sm" variant="outline" className="h-7 gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => whatsapp(o)} data-testid={`whatsapp-${o.number}`}>
                          <MessageCircle className="h-3 w-3" /> WhatsApp
                        </Button>
                      )}
                      {o.status !== "entregue" && o.status !== "cancelado" && (
                        <Button size="sm" variant="outline" className="h-7" onClick={() => cancel(o)}>Cancelar</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <OrderEditDialog order={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={load} products={products} />
    </div>
  );
}
